import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, ArrowRight, Loader2, Zap } from "lucide-react";
import Seo from "../components/Seo";

const WINDOWS = [
  { value: 7, label: "This week" },
  { value: 30, label: "30 days" },
  { value: 90, label: "All-time (90d)" },
];

export default function Leaderboard() {
  const [windowDays, setWindowDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/leaderboard", {
          params: { window_days: windowDays, limit: 50 },
        });
        if (!cancelled) setData(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const leaders = data?.leaders || [];

  return (
    <div className="min-h-screen bg-background grain relative" data-testid="leaderboard-page">
      <Seo
        title="Top Bankrolls · Sports Bettor Leaderboard · Martingale Playbook"
        path="/leaderboard"
        description="Live leaderboard of the top Martingale Playbook users — ranked by weekly percentage gain across MLB, NBA, NHL, NFL and UFC plays."
      />
      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
              <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
            </div>
            <div>
              <div className="font-display text-lg leading-none">Martingale</div>
              <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/register"
              data-testid="leaderboard-cta-register"
              className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#00D4FF] text-black hover:brightness-110"
            >
              Start free
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="overline mb-3 flex items-center gap-2">
          <Trophy size={12} strokeWidth={1.5} /> PUBLIC LEADERBOARD
        </div>
        <h1 className="font-display text-4xl lg:text-6xl leading-[0.95] tracking-tighter">
          Top bankrolls,{" "}
          <span className="text-[#00D4FF]">
            {WINDOWS.find((w) => w.value === windowDays)?.label.toLowerCase()}
          </span>.
        </h1>
        <p className="mt-5 text-zinc-400 max-w-2xl">
          These are Playbook members who chose to make their tracker public, ranked by
          percentage gain on their starting bankroll. Names only — no emails, no balances.
          <span className="block mt-2 amber-text text-sm">
            Opt in or out anytime from your Profile page.
          </span>
        </p>

        <div className="mt-8 flex flex-wrap gap-0 border-b border-zinc-800" data-testid="leaderboard-tabs">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              data-active={windowDays === w.value}
              onClick={() => setWindowDays(w.value)}
              data-testid={`leaderboard-window-${w.value}`}
              className="tab-terminal"
            >
              {w.label}
            </button>
          ))}
        </div>

        <div className="pt-8">
          {loading && !data ? (
            <div className="flex items-center gap-2 text-zinc-400 mono text-sm">
              <Loader2 size={16} className="animate-spin text-[#00D4FF]" /> Loading board…
            </div>
          ) : leaders.length === 0 ? (
            <div
              data-testid="leaderboard-empty"
              className="border border-dashed border-zinc-800 p-12 text-center"
            >
              <div className="font-display text-2xl text-zinc-300 mb-2">
                No public bankrolls yet
              </div>
              <p className="text-sm text-zinc-500 max-w-md mx-auto mb-6">
                Be the first on the board. Track a few bets, flip the switch on your
                profile, and the world gets to watch you win.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs"
              >
                Start tracking <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div
              className="border border-zinc-800 overflow-hidden"
              data-testid="leaderboard-table"
            >
              <table className="w-full mono text-sm">
                <thead className="bg-zinc-900">
                  <tr className="text-left">
                    <th className="px-4 py-3 overline">#</th>
                    <th className="px-4 py-3 overline">HANDLE</th>
                    <th className="px-4 py-3 overline text-right">% GAIN</th>
                    <th className="px-4 py-3 overline text-right">NET</th>
                    <th className="px-4 py-3 overline text-right">W·L</th>
                    <th className="px-4 py-3 overline text-right">WIN%</th>
                    <th className="px-4 py-3 overline text-right">STREAK</th>
                  </tr>
                </thead>
                <tbody>
                  {leaders.map((r, i) => (
                    <tr
                      key={`${r.handle}-${i}`}
                      data-testid={`leaderboard-row-${i}`}
                      className="border-t border-zinc-800"
                    >
                      <td className="px-4 py-3 text-zinc-500">{i + 1}</td>
                      <td className="px-4 py-3 text-white font-display text-lg">
                        {r.handle}
                        {i === 0 && (
                          <span className="ml-2 mono text-[10px] px-2 py-0.5 border border-[#00D4FF] text-[#00D4FF] tracking-widest">
                            TOP
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-display text-lg ${
                          r.pct_gain >= 0 ? "neon-text" : "text-red-400"
                        }`}
                      >
                        {r.pct_gain >= 0 ? "+" : ""}
                        {r.pct_gain}%
                      </td>
                      <td
                        className={`px-4 py-3 text-right ${
                          r.net_profit >= 0 ? "neon-text" : "text-red-400"
                        }`}
                      >
                        {r.net_profit >= 0 ? "+" : ""}${r.net_profit.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-zinc-300">
                        {r.wins}–{r.losses}
                      </td>
                      <td className="px-4 py-3 text-right text-zinc-400">
                        {r.win_rate_pct}%
                      </td>
                      <td className="px-4 py-3 text-right amber-text flex items-center justify-end gap-1">
                        <Zap size={12} strokeWidth={1.5} /> {r.longest_streak}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-12 border-t border-zinc-800 pt-10 text-sm text-zinc-400 leading-relaxed">
          <strong className="text-white">How it works:</strong> members enable the public
          leaderboard toggle in their profile and we publish their handle, record, and
          percentage gain against their starting bankroll. No emails. No exact bankrolls.
          Want on the board?{" "}
          <Link to="/register" className="neon-text underline">
            Start a free trial
          </Link>
          .
        </div>
      </main>

      <footer className="relative z-10 border-t border-zinc-800 py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-wrap justify-between gap-4 text-xs mono text-zinc-500">
          <div>© 2026 MARTINGALE PLAYBOOK</div>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-[#00D4FF]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#00D4FF]">Privacy</Link>
            <Link to="/responsible-gambling" className="hover:text-[#00D4FF]">Responsible Gambling</Link>
          </div>
          <div className="amber-text">21+ · GAMBLE RESPONSIBLY</div>
        </div>
      </footer>
    </div>
  );
}
